/**
 * MarketSearch — componente per la ricerca offerte di acquisto tramite AI
 */
import { useState } from 'react'
import {
  Button, Input, Modal, Spin, Empty, Tag, Typography, Space, Tooltip,
  Alert, Divider, Row, Col, Card, message, theme,
} from 'antd'
import {
  SearchOutlined, RobotOutlined, StarFilled, LinkOutlined,
  ShoppingOutlined, HistoryOutlined, CheckOutlined, CloseOutlined,
} from '@ant-design/icons'
import axiosInstance from '@/lib/axios'

const { Text } = Typography

// ── Tipi esportati ─────────────────────────────────────────────────────────

export interface SearchResult {
  id: number
  source: string
  source_url: string | null
  titolo: string
  fornitore: string | null
  prezzo: number | null
  valuta: string | null
  disponibilita: string | null
  spedizione: string | null
  immagine_url: string | null
  snippet: string | null
  ai_rank: number | null
  ai_score: number | null
  ai_note: string | null
  ai_recommended: boolean
}

interface SearchSession {
  id: number
  query: string
  vehicle_context: string | null
  status: string
  sources_used: string[]
  created_at: string
  completed_at: string | null
  results: SearchResult[]
}

interface MarketSearchProps {
  itemId?: number
  defaultQuery: string
  vehicleContext?: string
  /** Se true, renderizza solo l'icona (nessun testo). Usa Tooltip per accessibilità. */
  iconOnly?: boolean
  /** Callback quando l'utente sceglie un'offerta — riceve prezzo, fornitore, url e il risultato completo */
  onSelectOffer?: (price: number | null, supplier: string | null, url: string | null, result: SearchResult) => void
}

// ── Costanti ────────────────────────────────────────────────────────────────

export const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  serpapi_shopping: { label: 'Google Shopping', color: 'blue' },
  amazon: { label: 'Amazon', color: 'orange' },
  ebay: { label: 'eBay', color: 'gold' },
  tavily: { label: 'Web', color: 'purple' },
  tecdoc: { label: 'TecDoc', color: 'cyan' },
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export function formatEuroOffer(val: number | null) {
  if (val == null) return '—'
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val)
}

function ScoreTag({ score }: { score: number | null }) {
  if (score == null) return null
  const color = score >= 8 ? '#52c41a' : score >= 6 ? '#faad14' : '#ff4d4f'
  return (
    <span style={{ background: color, color: '#fff', borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>
      {score.toFixed(1)}
    </span>
  )
}

// ── ResultCard (usato sia nel modal che come anteprima) ─────────────────────

function ResultCard({
  result,
  onSelect,
  compact = false,
}: {
  result: SearchResult
  onSelect?: (r: SearchResult) => void
  compact?: boolean
}) {
  const { token } = theme.useToken()
  const src = SOURCE_LABELS[result.source] ?? { label: result.source, color: 'default' }
  return (
    <Card
      size="small"
      style={{
        marginBottom: compact ? 0 : 8,
        border: result.ai_recommended ? `2px solid ${token.colorSuccess}` : `1px solid ${token.colorBorder}`,
        background: result.ai_recommended ? token.colorSuccessBg : token.colorBgContainer,
        borderRadius: 8,
      }}
      bodyStyle={{ padding: compact ? '8px 12px' : '10px 14px' }}
    >
      <Row gutter={12} align="middle">
        {result.immagine_url && (
          <Col flex="56px">
            <img
              src={result.immagine_url}
              alt={result.titolo}
              loading="lazy"
              style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          </Col>
        )}

        <Col flex="1" style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
            {result.ai_recommended && (
              <Tag color="success" icon={<StarFilled />} style={{ margin: 0 }}>Consigliato</Tag>
            )}
            <Tag color={src.color} style={{ margin: 0 }}>{src.label}</Tag>
            {result.ai_rank != null && <Text type="secondary" style={{ fontSize: 11 }}>#{result.ai_rank}</Text>}
            <ScoreTag score={result.ai_score} />
          </div>
          <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, marginBottom: 2 }}>
            {result.titolo}
          </div>
          {result.ai_note && (
            <div style={{ fontSize: 11, color: token.colorTextTertiary, marginBottom: 2 }}>
              <RobotOutlined style={{ marginRight: 4 }} />{result.ai_note}
            </div>
          )}
          <Space size={12} wrap style={{ fontSize: 11, color: token.colorTextSecondary }}>
            {result.fornitore && <span>{result.fornitore}</span>}
            {result.disponibilita && <span>{result.disponibilita}</span>}
            {result.spedizione && <span>Sped.: {result.spedizione}</span>}
          </Space>
        </Col>

        <Col style={{ textAlign: 'right', minWidth: 110 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: token.colorPrimary, marginBottom: 6 }}>
            {formatEuroOffer(result.prezzo)}
          </div>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {onSelect && (
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => onSelect(result)}
                style={{ width: '100%' }}
              >
                Usa offerta
              </Button>
            )}
            <Button
              size="small"
              icon={<LinkOutlined />}
              onClick={() => {
                const url = result.source_url
                  || `https://www.google.com/search?q=${encodeURIComponent(result.titolo)}`
                window.open(url, '_blank', 'noopener,noreferrer')
              }}
              style={{ width: '100%' }}
            >
              Apri sito
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  )
}

// ── OfferPreviewBanner — card cliccabile mostrata nell'item RDA dopo selezione

export function OfferPreviewBanner({
  result,
  onRemove,
}: {
  result: SearchResult
  onRemove: () => void
}) {
  const { token } = theme.useToken()
  const src = SOURCE_LABELS[result.source] ?? { label: result.source, color: 'default' }
  const openUrl = () => {
    const url = result.source_url
      || `https://www.google.com/search?q=${encodeURIComponent(result.titolo)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <div style={{ position: 'relative', marginBottom: 6 }}>
      {/* Tasto rimozione */}
      <Button
        type="text"
        size="small"
        icon={<CloseOutlined />}
        onClick={(e) => { e.stopPropagation(); onRemove() }}
        style={{
          position: 'absolute',
          top: 4,
          right: 4,
          zIndex: 1,
          color: token.colorTextTertiary,
          padding: '0 4px',
          height: 20,
          fontSize: 11,
        }}
        title="Rimuovi offerta selezionata"
      />

      <Card
        size="small"
        onClick={openUrl}
        style={{
          border: result.ai_recommended ? `2px solid ${token.colorSuccess}` : `1px solid ${token.colorBorder}`,
          background: result.ai_recommended ? token.colorSuccessBg : token.colorBgContainer,
          borderRadius: 8,
          cursor: 'pointer',
        }}
        bodyStyle={{ padding: '8px 32px 8px 10px' }}
      >
        <Row gutter={10} align="middle" wrap={false}>
          {result.immagine_url && (
            <Col flex="44px">
              <img
                src={result.immagine_url}
                alt=""
                loading="lazy"
                style={{ width: 38, height: 38, objectFit: 'contain', borderRadius: 3 }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            </Col>
          )}
          <Col flex="1" style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 1 }}>
              <Tag color={src.color} style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{src.label}</Tag>
              {result.ai_recommended && <Tag color="success" icon={<StarFilled />} style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>Consigliato</Tag>}
              <ScoreTag score={result.ai_score} />
            </div>
            <div style={{ fontWeight: 600, fontSize: 12, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {result.titolo}
            </div>
            {result.ai_note && (
              <div style={{ fontSize: 10, color: token.colorTextTertiary }}>
                <RobotOutlined style={{ marginRight: 3 }} />{result.ai_note}
              </div>
            )}
          </Col>
          <Col style={{ textAlign: 'right', minWidth: 70 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: token.colorPrimary }}>
              {formatEuroOffer(result.prezzo)}
            </div>
            <div style={{ fontSize: 10, color: token.colorPrimary }}>
              <LinkOutlined /> Apri sito
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

// ── Componente principale ───────────────────────────────────────────────────

export function MarketSearch({ itemId, defaultQuery, vehicleContext, iconOnly, onSelectOffer }: MarketSearchProps) {
  const { token } = theme.useToken()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(defaultQuery)
  const [loading, setLoading] = useState(false)
  const [session, setSession] = useState<SearchSession | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [history, setHistory] = useState<SearchSession[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const handleSearch = async () => {
    setLoading(true)
    setError(null)
    setSession(null)
    try {
      const res = await axiosInstance.post('/procurement/search', {
        query,
        vehicle_context: vehicleContext || null,
        purchase_request_item_id: itemId || null,
      })
      setSession(res.data)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore durante la ricerca')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenHistory = async () => {
    if (!itemId) return
    setHistoryLoading(true)
    setHistoryOpen(true)
    try {
      const res = await axiosInstance.get(`/procurement/item/${itemId}/sessions`)
      setHistory(res.data)
    } catch {
      setHistory([])
    } finally {
      setHistoryLoading(false)
    }
  }

  const handleSelectOffer = (result: SearchResult) => {
    if (onSelectOffer) {
      onSelectOffer(result.prezzo, result.fornitore, result.source_url, result)
      message.success(`Offerta selezionata: ${formatEuroOffer(result.prezzo)}`)
      setOpen(false)
    }
  }

  return (
    <>
      {iconOnly ? (
        <Tooltip title="Cerca offerte">
          <Button
            type="text"
            size="small"
            icon={<ShoppingOutlined />}
            onClick={() => { setOpen(true); setQuery(defaultQuery) }}
            style={{ padding: '0 4px', color: '#1677ff' }}
          />
        </Tooltip>
      ) : (
        <Button
          type="default"
          size="small"
          icon={<ShoppingOutlined />}
          onClick={() => { setOpen(true); setQuery(defaultQuery) }}
          style={{ fontSize: 11 }}
        >
          Cerca offerte
        </Button>
      )}

      <Modal
        title={<Space><RobotOutlined style={{ color: token.colorPrimary }} /><span>Ricerca Offerte di Mercato</span></Space>}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={740}
        styles={{ body: { maxHeight: '72vh', overflowY: 'auto' } }}
      >
        {vehicleContext && (
          <div style={{ marginBottom: 10, fontSize: 12, color: token.colorTextTertiary }}>
            <strong>Veicolo:</strong> {vehicleContext}
          </div>
        )}

        {onSelectOffer && (
          <Alert
            type="info"
            showIcon
            message="Clicca «Usa offerta» per compilare automaticamente prezzo e fornitore nell'articolo RDO."
            style={{ marginBottom: 10, fontSize: 12 }}
          />
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onPressEnter={handleSearch}
            placeholder="es. pastiglie freno anteriori Fiat Punto 1.2"
            style={{ flex: 1 }}
            prefix={<SearchOutlined />}
          />
          <Button type="primary" icon={<RobotOutlined />} loading={loading} onClick={handleSearch}>
            Cerca con AI
          </Button>
          {itemId && (
            <Tooltip title="Ricerche precedenti">
              <Button icon={<HistoryOutlined />} onClick={handleOpenHistory} />
            </Tooltip>
          )}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: '#888' }}>Ricerca in corso su più fonti…</div>
          </div>
        )}

        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />}

        {session && !loading && (
          <>
            <div style={{ marginBottom: 10, fontSize: 12, color: '#555' }}>
              <Text type="secondary">
                Fonti: {(session.sources_used || []).map(s => SOURCE_LABELS[s]?.label ?? s).join(', ') || 'nessuna'}
                {' · '}{session.results.length} risultati
              </Text>
            </div>
            {session.results.length === 0 ? (
              <Empty
                description="Nessun risultato trovato. Prova a modificare la query o verificare le API key nelle impostazioni."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              session.results.map(r => (
                <ResultCard key={r.id} result={r} onSelect={onSelectOffer ? handleSelectOffer : undefined} />
              ))
            )}
          </>
        )}

        {!session && !loading && !error && (
          <Empty description="Inserisci una descrizione del ricambio e clicca 'Cerca con AI'" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}

        <Divider style={{ margin: '12px 0 4px 0' }} />
        <div style={{ fontSize: 11, color: '#aaa', textAlign: 'center' }}>
          I prezzi sono indicativi. Verifica sempre disponibilità e compatibilità con il fornitore.
        </div>
      </Modal>

      {/* History modal */}
      <Modal
        title={<Space><HistoryOutlined /><span>Ricerche precedenti</span></Space>}
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        width={720}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : history.length === 0 ? (
          <Empty description="Nessuna ricerca precedente" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          history.map(s => (
            <Card
              key={s.id}
              size="small"
              style={{ marginBottom: 8 }}
              title={
                <Space>
                  <Text strong>{s.query}</Text>
                  <Tag color={s.status === 'done' ? 'success' : 'error'}>{s.status}</Tag>
                  <Text type="secondary" style={{ fontSize: 11 }}>{new Date(s.created_at).toLocaleString('it-IT')}</Text>
                </Space>
              }
              extra={
                <Button size="small" onClick={() => { setSession(s); setHistoryOpen(false); setOpen(true) }}>
                  Visualizza
                </Button>
              }
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                {s.results.length} risultati · Fonti: {(s.sources_used || []).map(x => SOURCE_LABELS[x]?.label ?? x).join(', ') || '—'}
              </Text>
            </Card>
          ))
        )}
      </Modal>
    </>
  )
}

export default MarketSearch
